import { format } from "date-fns";

import {
  BillingTransaction,
  BillingPaymentMethod,
  BillingTransactionGuestSummary,
  BillingTransactionStatus,
  BillingTransactionType,
} from "@/types/billing-transactions";

export type BillingTransactionsExportRow = {
  "Created At": string;
  "Completed At": string;
  "Transaction ID": string;
  "Guest Name": string;
  "Guest Initials": string;
  "Guest ID": string;
  "Guest Email": string;
  "Guest Phone": string;
  "Booking ID": string;
  Type: BillingTransactionType;
  Status: BillingTransactionStatus;
  "Payment Method": BillingPaymentMethod;
  Amount: string;
  Currency: string;
  "Application Fee": string;
  "Net Amount": string;
  "Hotel ID": string;
  "Stripe Payment Intent ID": string;
  "Stripe Charge ID": string;
  "Failure Message": string;
};

const formatDateTime = (value?: string | Date | null) => {
  if (!value) return "";

  try {
    return format(new Date(value), "yyyy-MM-dd HH:mm:ss");
  } catch {
    return String(value);
  }
};

const formatAmount = (amount?: number | null, currency?: string | null) => {
  if (amount === null || amount === undefined) return "";

  const code = currency || "";
  const suffix = code ? ` ${code}` : "";

  return `${(amount / 100).toFixed(2)}${suffix}`;
};

const escapeCell = (value: unknown) => {
  const stringValue = String(value ?? "");

  return stringValue
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

const escapeCsvField = (value: unknown) => {
  const stringValue = String(value ?? "");

  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

const buildDisplayName = (guest?: BillingTransactionGuestSummary | null) => {
  if (!guest) return "";
  if (guest.displayName) return guest.displayName;

  const parts = [guest.firstName, guest.lastName].filter(Boolean);
  return parts.join(" ");
};

export const getBillingTransactionGuestSummary = (
  transaction: BillingTransaction,
): BillingTransactionGuestSummary | null => {
  if (transaction.guestSummary) return transaction.guestSummary;
  if (transaction.booking?.guest) return transaction.booking.guest;

  const billingItemGuest = transaction.billingItems?.find(
    (item) => item.guest,
  )?.guest;

  return billingItemGuest || null;
};

export const mapBillingTransactionsForExport = (
  transactions: BillingTransaction[],
): BillingTransactionsExportRow[] =>
  transactions.map((transaction) => {
    const guestSummary = getBillingTransactionGuestSummary(transaction);
    const billingItemGuestIds =
      transaction.billingItems
        ?.map((item) => item.guestId)
        .filter(Boolean)
        .join(", ") ?? "";

    return {
      "Created At": formatDateTime(transaction.createdAt),
      "Completed At": formatDateTime(transaction.completedAt),
      "Transaction ID": transaction.id,
      "Guest Name": buildDisplayName(guestSummary),
      "Guest Initials": guestSummary?.initials || "",
      "Guest ID": transaction.booking?.guestId || billingItemGuestIds,
      "Guest Email": guestSummary?.email || "",
      "Guest Phone": guestSummary?.phone || "",
      "Booking ID": transaction.bookingId || transaction.booking?.id || "",
      Type: transaction.type,
      Status: transaction.status,
      "Payment Method": transaction.paymentMethod,
      Amount: formatAmount(transaction.amount, transaction.currency),
      Currency: transaction.currency,
      "Application Fee": formatAmount(
        transaction.applicationFeeAmount,
        transaction.currency,
      ),
      "Net Amount": formatAmount(transaction.netAmount, transaction.currency),
      "Hotel ID": transaction.hotelId,
      "Stripe Payment Intent ID": transaction.stripePaymentIntentId || "",
      "Stripe Charge ID": transaction.stripeChargeId || "",
      "Failure Message": transaction.failureMessage || "",
    };
  });

export const downloadBillingTransactionsCsv = (
  rows: BillingTransactionsExportRow[],
  filename: string,
) => {
  if (rows.length === 0) return;

  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      headers
        .map((header) => escapeCsvField(row[header as keyof typeof row]))
        .join(","),
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadBillingTransactionsExcel = (
  rows: BillingTransactionsExportRow[],
  filename: string,
) => {
  if (rows.length === 0) return;

  const headers = Object.keys(rows[0]);
  const tableHeader = headers
    .map((header) => `<th>${escapeCell(header)}</th>`)
    .join("");
  const tableRows = rows
    .map(
      (row) =>
        `<tr>${headers
          .map(
            (header) =>
              `<td>${escapeCell(row[header as keyof typeof row])}</td>`,
          )
          .join("")}</tr>`,
    )
    .join("");

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head>
        <meta charset="UTF-8" />
        <style>
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #d4d4d8; padding: 8px; text-align: left; }
          th { background: #f4f4f5; font-weight: 700; }
        </style>
      </head>
      <body>
        <table>
          <thead>
            <tr>${tableHeader}</tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
    </html>
  `.trim();

  const blob = new Blob([html], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
